import { useState } from "react";
import { useNavigate } from "react-router-dom";

interface Props {
  onSwitchToSns?: () => void;
}

type Mode = "menu" | "press" | "sns" | "done";

// ── 보도자료 실습 필드 ──────────────────────────────────
const pressFields = [
  { id: "p1", label: "보도자료 제목 (헤드라인)", type: "text", placeholder: "예: 루나&스타즈, 1st EP 'Starlight' 오는 4월 15일 공식 발매", hint: "핵심 정보(아티스트명·제품명·날짜)를 30자 이내로 압축하세요.", required: true },
  { id: "p2", label: "발신처 (회사 및 배포일)", type: "text", placeholder: "예: K-ArtPEN 엔터테인먼트 | 2026.04.01", required: true },
  { id: "p3", label: "리드 문장 (Lead) — 첫 2~3문장", type: "textarea", placeholder: "누가(Who) · 무엇을(What) · 언제(When) · 어디서(Where) · 왜(Why)를 첫 단락에 압축하세요.\n예) K-ArtPEN 엔터테인먼트 소속 신인 그룹 루나&스타즈가 오는 4월 15일 1st EP 'Starlight'를 발매한다. 이번 앨범은 청춘의 빛나는 순간을 테마로…", hint: "기자들은 첫 단락만 읽어도 기사화 여부를 결정합니다. 5W1H를 반드시 포함하세요.", required: true },
  { id: "p4", label: "주요 내용 (본문)", type: "textarea", placeholder: "발매 배경, 앨범 구성, 제작 비하인드, 아티스트 코멘트 등 구체적인 내용을 2~3문단으로 작성하세요.", required: true },
  { id: "p5", label: "공식 인용구 (Quote)", type: "textarea", placeholder: '예: K-ArtPEN 대표는 "루나&스타즈의 데뷔는 국내 K-POP 씬에 새로운 바람을 불러올 것"이라며 "글로벌 팬덤을 목표로 장기적인 아티스트 빌드업을 진행하겠다"고 말했다.', hint: "보도자료에서 인용구는 신뢰도와 기사화 확률을 크게 높입니다.", required: false },
  { id: "p6", label: "담당자 연락처 / 문의처", type: "text", placeholder: "예: 마케팅팀 김소현 | press@karten.co.kr | 02-1234-5678", required: true },
];

// ── SNS 카피 실습 필드 ─────────────────────────────────
const snsFields = [
  { id: "s0", label: "타겟 플랫폼", type: "select", options: ["인스타그램 (릴스·피드)", "틱톡 (쇼츠)", "X (트위터)", "유튜브 커뮤니티"], required: true, placeholder: "" },
  { id: "s1", label: "후킹 첫 문장 (Hook)", type: "text", placeholder: "예: 이 곡 한 번 듣고 나면 하루 종일 머릿속에서 안 떠나요 ㅠㅠ", hint: "첫 1~2초가 스크롤을 멈추게 합니다. 질문·공감·충격·유머 중 하나로 시작하세요.", required: true },
  { id: "s2", label: "핵심 메시지 (본문)", type: "textarea", placeholder: "본문에 들어갈 내용을 자유롭게 작성하세요. 자연스럽고 진정성 있는 언어를 사용하고, 과도한 홍보 표현은 피하세요.", hint: "인스타그램 캡션 최적 길도: 125자 이내. 틱톡: 150자 이내.", required: true },
  { id: "s3", label: "해시태그 (5~10개)", type: "text", placeholder: "예: #루나앤스타즈 #Starlight #KPOP #NewMusic #케이팝 #신보 #LunaAndStars", hint: "대·소 카테고리 해시태그를 섞고, 브랜드 고유 해시태그 1개는 필수입니다.", required: true },
  { id: "s4", label: "CTA (Call-to-Action)", type: "text", placeholder: "예: 지금 스포티파이에서 감상하세요 🎵 | 링크 바이오 클릭 | 댓글로 최애 트랙 알려주세요!", hint: "CTA가 명확할수록 팔로워 행동 전환율이 올라갑니다.", required: true },
  { id: "s5", label: "이모지 포인트 선택", type: "text", placeholder: "예: 🌟 ✨ 🎵 💜 🔥 — 2~4개만 사용. 과도한 이모지는 도달률을 낮춥니다.", required: false },
];

const inputCls = "w-full bg-[#1C1F2E] border border-[#2A2D3E] focus:border-[#4B6CF7]/50 outline-none text-[#DDDDDD] text-xs px-3 py-2 rounded-lg placeholder-[#555566] transition-colors";
const textareaCls = "w-full bg-[#1C1F2E] border border-[#2A2D3E] focus:border-[#4B6CF7]/50 outline-none text-[#DDDDDD] text-xs px-3 py-2.5 rounded-lg placeholder-[#555566] transition-colors resize-none leading-relaxed";
const selectCls = "w-full bg-[#1C1F2E] border border-[#2A2D3E] focus:border-[#4B6CF7]/50 outline-none text-[#DDDDDD] text-xs px-3 py-2 rounded-lg cursor-pointer transition-colors";

export default function PracticePanel({ onSwitchToSns }: Props) {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("menu");
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  const fields = mode === "press" ? pressFields : snsFields;
  const allRequired = fields.filter(f => f.required).every(f => values[f.id]?.trim());

  const set = (id: string, val: string) => setValues(prev => ({ ...prev, [id]: val }));

  const handleSubmit = () => {
    const isSnsMode = mode === "sns";
    if (isSnsMode) {
      // SNS 카피 완료 시 초안 localStorage에 저장
      const hook = values["s1"] ?? "";
      const body = values["s2"] ?? "";
      const tags = values["s3"] ?? "";
      const emoji = values["s5"] ?? "";
      const combined = [hook, body, tags, emoji].filter(Boolean).join("\n").trim();
      try {
        localStorage.setItem("kap_sns_draft", combined);
        localStorage.setItem("kap_sns_platform", values["s0"] ?? "");
      } catch { /* ignore */ }
    }
    setSubmitted(true);
    setMode("done");
  };

  const reset = () => {
    setMode("menu");
    setValues({});
    setSubmitted(false);
  };

  // ── 메뉴 선택 ──────────────────────────────────────────
  if (mode === "menu") {
    return (
      <div className="flex flex-col h-full overflow-y-auto py-2">
        <p className="text-[#555566] text-[10px] mb-4">실제 업무에서 사용하는 마케팅 문서를 직접 작성해보세요.</p>
        <div className="space-y-3">
          {[
            {
              key: "press" as const,
              icon: "ri-newspaper-line",
              title: "보도자료 직접 작성하기",
              desc: "5W1H 구조·리드문장·인용구까지 실제 보도자료 양식 완성",
              tag: "실무 필수",
              color: "text-[#E8375F]",
              bg: "bg-[#E8375F]/10",
              border: "border-[#E8375F]/20",
            },
            {
              key: "sns" as const,
              icon: "ri-instagram-line",
              title: "SNS 카피라이팅 실습",
              desc: "Hook·본문·해시태그·CTA까지 플랫폼별 SNS 카피 완성",
              tag: "바이럴 전략",
              color: "text-[#7A9BFF]",
              bg: "bg-[#4B6CF7]/10",
              border: "border-[#4B6CF7]/20",
            },
          ].map(item => (
            <button key={item.key} type="button" onClick={() => setMode(item.key)}
              className={`w-full text-left p-4 rounded-xl border ${item.border} ${item.bg} hover:opacity-90 cursor-pointer transition-all`}>
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 flex items-center justify-center rounded-lg bg-white/[0.04] shrink-0`}>
                  <i className={`${item.icon} ${item.color} text-base`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[#CCCCCC] text-xs font-semibold">{item.title}</span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${item.bg} ${item.color} border ${item.border} font-bold`}>{item.tag}</span>
                  </div>
                  <p className="text-[#555566] text-[11px] leading-snug">{item.desc}</p>
                </div>
                <i className="ri-arrow-right-line text-[#444444] text-sm shrink-0 mt-2"></i>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-4 bg-[#1C1F2E] border border-white/[0.04] rounded-xl p-3">
          <div className="flex items-center gap-2 mb-2">
            <i className="ri-lightbulb-flash-line text-amber-400/70 text-sm"></i>
            <span className="text-[#888888] text-[10px] font-semibold">실무 팁</span>
          </div>
          <p className="text-[#555566] text-[10px] leading-relaxed">
            보도자료는 언론사 기자가 그대로 기사화할 수 있는 수준으로 작성해야 합니다. SNS 카피는 첫 1초 안에 스크롤을 멈추게 하는 훅(Hook)이 핵심입니다.
          </p>
        </div>
      </div>
    );
  }

  // ── 완료 화면 ──────────────────────────────────────────
  if (mode === "done") {
    const isSns = Object.keys(values).some(k => k.startsWith("s"));
    return (
      <div className="flex flex-col h-full overflow-y-auto py-2">
        <div className="flex flex-col items-center py-6 text-center mb-4">
          <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-emerald-400/10 border border-emerald-400/20 mb-3">
            <i className="ri-checkbox-circle-fill text-emerald-400 text-2xl"></i>
          </div>
          <h3 className="text-[#EEEEEE] font-bold text-sm mb-1">
            {isSns ? "SNS 카피 작성 완료!" : "보도자료 작성 완료!"}
          </h3>
          <p className="text-[#555566] text-xs">실제 배포 전 팀장 검토 후 발송하세요.</p>
        </div>

        <div className="bg-[#1C1F2E] border border-white/[0.05] rounded-xl p-4 space-y-3 mb-4">
          {fields.filter(f => values[f.id]).map(f => (
            <div key={f.id}>
              <div className="text-[#555566] text-[9px] font-semibold uppercase tracking-widest mb-1">{f.label}</div>
              <p className="text-[#CCCCCC] text-xs leading-relaxed whitespace-pre-line line-clamp-3">{values[f.id]}</p>
            </div>
          ))}
        </div>

        <div className="bg-amber-400/[0.06] border border-amber-400/15 rounded-xl p-3 mb-4">
          <div className="flex items-center gap-2 mb-1.5">
            <i className="ri-star-line text-amber-400/70 text-sm"></i>
            <span className="text-amber-400/80 text-[11px] font-semibold">다음 단계</span>
          </div>
          <p className="text-[#888888] text-[11px] leading-relaxed">
            {isSns ? "① 팀 내부 검토 → ② 아티스트 최종 확인 → ③ 예약 업로드 설정 → ④ 댓글 모니터링 대응" : "① 팀장 검토 → ② 아티스트/법무 확인 → ③ 배포 대상 언론사 선정 → ④ 배포 후 기사화 여부 추적"}
          </p>
        </div>

        <button type="button" onClick={reset}
          className="w-full py-2.5 bg-[#1C1F2E] border border-white/[0.06] hover:bg-white/[0.04] text-[#CCCCCC] text-xs rounded-xl cursor-pointer transition-colors whitespace-nowrap">
          <i className="ri-refresh-line mr-1.5"></i>다른 실습 도전하기
        </button>
        {!isSns && (
          <button type="button"
            onClick={() => navigate("/approval", { state: { templateId: "tmpl10" } })}
            className="w-full py-2.5 bg-[#4B6CF7] hover:bg-[#5B7CF7] text-white text-xs rounded-xl cursor-pointer transition-colors whitespace-nowrap font-semibold flex items-center justify-center gap-2">
            <i className="ri-send-plane-line"></i>실습 내용으로 결재 올리기
          </button>
        )}
        {isSns && onSwitchToSns && (
          <button type="button"
            onClick={() => { onSwitchToSns(); }}
            className="w-full py-2.5 bg-[#E8375F] hover:bg-[#d42e52] text-white text-xs rounded-xl cursor-pointer transition-colors whitespace-nowrap font-semibold flex items-center justify-center gap-2">
            <i className="ri-instagram-line"></i>SNS 패널에서 바로 포스팅하기
          </button>
        )}
      </div>
    );
  }

  // ── 작성 폼 ────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full">
      {/* 헤더 */}
      <div className="flex items-center gap-3 mb-3 pb-3 border-b border-white/[0.05] shrink-0">
        <button type="button" onClick={reset} className="text-[#555566] hover:text-[#CCCCCC] cursor-pointer transition-colors text-xs flex items-center gap-1">
          <i className="ri-arrow-left-line"></i>
        </button>
        <div>
          <p className="text-[#CCCCCC] text-xs font-semibold">
            {mode === "press" ? "보도자료 작성 실습" : "SNS 카피라이팅 실습"}
          </p>
          <p className="text-[#555566] text-[10px]">필수 항목(*) 입력 후 완료 버튼 클릭</p>
        </div>
      </div>

      {/* 필드 */}
      <div className="flex-1 overflow-y-auto space-y-4 pr-0.5">
        {fields.map(f => (
          <div key={f.id}>
            <label className="block text-[10px] font-semibold text-[#888888] uppercase tracking-wide mb-1.5">
              {f.label}{f.required && <span className="text-[#E8375F] ml-1">*</span>}
            </label>
            {f.type === "textarea" ? (
              <textarea value={values[f.id] ?? ""} onChange={e => set(f.id, e.target.value)}
                placeholder={f.placeholder} rows={4} maxLength={500} className={textareaCls} />
            ) : f.type === "select" ? (
              <select value={values[f.id] ?? ""} onChange={e => set(f.id, e.target.value)} className={selectCls}>
                <option value="">선택하세요</option>
                {f.options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            ) : (
              <input type="text" value={values[f.id] ?? ""} onChange={e => set(f.id, e.target.value)}
                placeholder={f.placeholder} className={inputCls} />
            )}
            {f.hint && (
              <div className="flex gap-1.5 mt-1.5">
                <i className="ri-lightbulb-flash-line text-amber-400/60 text-[10px] mt-0.5 shrink-0"></i>
                <p className="text-[#555566] text-[10px] leading-relaxed">{f.hint}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* 하단 버튼 */}
      <div className="shrink-0 pt-3 border-t border-white/[0.05] mt-3 flex items-center justify-between">
        <span className="text-[10px] text-[#444444]">
          {fields.filter(f => f.required && values[f.id]?.trim()).length}/{fields.filter(f => f.required).length} 완료
        </span>
        <button type="button" onClick={handleSubmit} disabled={!allRequired}
          className="flex items-center gap-1.5 px-4 py-2 bg-[#4B6CF7] hover:bg-[#5B7CF7] disabled:opacity-30 disabled:cursor-not-allowed text-white text-xs rounded-lg cursor-pointer transition-colors whitespace-nowrap font-semibold">
          <i className="ri-check-line"></i>작성 완료
        </button>
      </div>
    </div>
  );
}
